import { useRef, useCallback, useState } from "react";
import { toPng } from "html-to-image";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";

/* ───────── iPhone frame ───────── */
const IPhoneFrame = ({ children }: { children: React.ReactNode }) => (
  <div className="relative mx-auto" style={{ width: 300, height: 650 }}>
    {/* Outer bezel */}
    <div
      className="absolute inset-0 rounded-[3rem] border-[3px] border-foreground/80 overflow-hidden"
      style={{ background: "#FDFBF7" }}
    >
      {/* Dynamic Island */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[90px] h-[28px] bg-foreground rounded-full z-30" />
      {/* Screen content */}
      <div className="absolute inset-[3px] rounded-[2.75rem] overflow-hidden">
        {children}
      </div>
    </div>
  </div>
);

/* ───────── Screen 1: Hero ───────── */
const HeroScreen = () => (
  <div className="h-full flex flex-col items-center justify-center px-6 bg-background relative">
    {/* Subtle map-like dots */}
    <div className="absolute inset-0 opacity-[0.04]">
      {[...Array(30)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 rounded-full bg-foreground"
          style={{
            top: `${10 + Math.random() * 80}%`,
            left: `${10 + Math.random() * 80}%`,
          }}
        />
      ))}
    </div>
    <div className="relative z-10 text-center">
      <p className="funnel-display text-foreground text-[28px] font-bold leading-tight">
        find your
      </p>
      <p className="funnel-display text-foreground text-[28px] font-bold leading-tight">
        quiet spot
      </p>
      <p className="font-cormorant italic text-accent text-[22px] mt-1">
        anywhere.
      </p>
      <div className="mt-6 w-16 h-[2px] bg-foreground/20 mx-auto" />
      <p className="text-muted-foreground text-xs mt-4 max-w-[180px] mx-auto leading-relaxed">
        private, soundproof pods in the places you already are
      </p>
    </div>
    {/* Bottom logo */}
    <p className="absolute bottom-10 funnel-display text-foreground/30 text-sm font-bold">
      qwip
    </p>
  </div>
);

/* ───────── Screen 2: Map Discovery ───────── */
const MapScreen = () => {
  const markers = [
    { emoji: "☕", top: "30%", left: "25%" },
    { emoji: "📚", top: "45%", left: "60%" },
    { emoji: "🏨", top: "55%", left: "35%" },
    { emoji: "☕", top: "38%", left: "72%" },
    { emoji: "🏋️", top: "65%", left: "50%" },
    { emoji: "☕", top: "25%", left: "50%" },
    { emoji: "📚", top: "70%", left: "28%" },
    { emoji: "🏨", top: "48%", left: "42%" },
  ];

  return (
    <div className="h-full flex flex-col bg-[#e8e4d9] relative overflow-hidden">
      {/* Status bar spacer */}
      <div className="h-12" />
      {/* Category pills */}
      <div className="flex gap-2 px-4 py-2 z-10">
        {["☕ Cafés", "🏨 Hotels", "📚 Libraries", "🏋️ Gyms"].map((cat) => (
          <div
            key={cat}
            className="bg-background/90 backdrop-blur rounded-full px-3 py-1.5 text-[10px] font-medium text-foreground shadow-sm border border-border"
          >
            {cat}
          </div>
        ))}
      </div>
      {/* Fake map grid */}
      <div className="flex-1 relative">
        {/* Roads */}
        <div className="absolute top-0 left-[40%] w-[1px] h-full bg-foreground/10" />
        <div className="absolute top-[50%] left-0 w-full h-[1px] bg-foreground/10" />
        <div className="absolute top-0 left-[65%] w-[1px] h-full bg-foreground/10 rotate-12 origin-top" />
        <div className="absolute top-[30%] left-0 w-full h-[1px] bg-foreground/10 -rotate-6 origin-left" />
        {/* Markers */}
        {markers.map((m, i) => (
          <div
            key={i}
            className="absolute text-lg drop-shadow-md"
            style={{ top: m.top, left: m.left }}
          >
            {m.emoji}
          </div>
        ))}
        {/* Selected marker highlight */}
        <div
          className="absolute flex flex-col items-center"
          style={{ top: "43%", left: "39%" }}
        >
          <div className="bg-foreground text-background text-[10px] px-2 py-1 rounded-lg font-medium shadow-lg mb-1">
            The Hoxton
          </div>
          <div className="w-3 h-3 bg-foreground rounded-full border-2 border-background shadow-md" />
        </div>
      </div>
    </div>
  );
};

/* ───────── Screen 3: Spot Detail ───────── */
const SpotDetailScreen = () => (
  <div className="h-full flex flex-col bg-[#e8e4d9] relative">
    <div className="h-12" />
    {/* Fake map top */}
    <div className="flex-1 relative">
      <div className="absolute top-[30%] left-[45%] w-3 h-3 bg-foreground rounded-full border-2 border-background" />
    </div>
    {/* Bottom sheet */}
    <div className="bg-background rounded-t-3xl p-5 pb-8 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
      <div className="w-10 h-1 bg-foreground/15 rounded-full mx-auto mb-4" />
      {/* Image placeholder */}
      <div className="w-full h-28 rounded-2xl bg-foreground/5 mb-3 flex items-center justify-center overflow-hidden">
        <span className="text-4xl">☕</span>
      </div>
      <h3 className="funnel-display text-foreground text-lg font-bold">
        Caravan King's Cross
      </h3>
      <p className="text-muted-foreground text-xs mt-0.5">
        1 Granary Square, N1C 4AA
      </p>
      {/* Badges */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {[
          { label: "📶 Fast WiFi", color: "bg-primary/10 text-foreground" },
          { label: "🔇 Quiet", color: "bg-secondary/30 text-foreground" },
          { label: "📞 Call-friendly", color: "bg-accent/15 text-foreground" },
          { label: "🔌 Power", color: "bg-primary/10 text-foreground" },
        ].map((b) => (
          <span
            key={b.label}
            className={`${b.color} text-[9px] font-medium px-2 py-1 rounded-full`}
          >
            {b.label}
          </span>
        ))}
      </div>
      {/* Noise bar */}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground">Noise</span>
        <div className="flex-1 h-1.5 bg-foreground/10 rounded-full overflow-hidden">
          <div className="h-full w-[25%] bg-primary rounded-full" />
        </div>
        <span className="text-[10px] text-foreground font-medium">Quiet</span>
      </div>
    </div>
  </div>
);

/* ───────── Screen 4: Smart Search ───────── */
const SearchScreen = () => (
  <div className="h-full flex flex-col bg-background relative">
    <div className="h-14" />
    <div className="px-4">
      {/* Search bar */}
      <div className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-2 shadow-sm">
        <span className="text-muted-foreground text-sm">🔍</span>
        <span className="text-foreground text-sm font-medium">Kings Cross</span>
        <span className="animate-pulse text-foreground">|</span>
      </div>
      {/* Dropdown */}
      <div className="mt-2 bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
        {[
          { name: "Caravan King's Cross", type: "☕ Café", dist: "0.2 km" },
          { name: "The Lighterman", type: "🍽️ Restaurant", dist: "0.3 km" },
          { name: "Google Campus", type: "💼 Coworking", dist: "0.5 km" },
          { name: "British Library", type: "📚 Library", dist: "0.8 km" },
        ].map((r, i) => (
          <div
            key={r.name}
            className={`flex items-center justify-between px-4 py-3 ${
              i === 0 ? "bg-primary/5" : ""
            } ${i < 3 ? "border-b border-border" : ""}`}
          >
            <div>
              <p className="text-foreground text-xs font-medium">{r.name}</p>
              <p className="text-muted-foreground text-[10px]">{r.type}</p>
            </div>
            <span className="text-muted-foreground text-[10px]">{r.dist}</span>
          </div>
        ))}
        {/* Search on map action */}
        <div className="px-4 py-3 bg-primary/5 flex items-center gap-2">
          <span className="text-[10px]">📍</span>
          <span className="text-foreground text-[10px] font-medium">
            Search "Kings Cross" on map
          </span>
        </div>
      </div>
    </div>
  </div>
);

/* ───────── Screen 5: Categories ───────── */
const CategoriesScreen = () => {
  const cats = [
    { emoji: "☕", name: "cafés", count: 42, color: "bg-accent/10" },
    { emoji: "🏨", name: "hotels", count: 18, color: "bg-secondary/20" },
    { emoji: "📚", name: "libraries", count: 12, color: "bg-primary/10" },
    { emoji: "💼", name: "coworking", count: 24, color: "bg-accent/10" },
    { emoji: "🏋️", name: "gyms", count: 8, color: "bg-secondary/20" },
    { emoji: "🍽️", name: "restaurants", count: 15, color: "bg-primary/10" },
  ];

  return (
    <div className="h-full flex flex-col bg-background relative">
      <div className="h-14" />
      <div className="px-5 mt-2">
        <p className="funnel-display text-foreground text-xl font-bold">
          browse by type
        </p>
        <p className="text-muted-foreground text-xs mt-1">
          find the perfect work spot for you
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 px-5 mt-5">
        {cats.map((c) => (
          <div
            key={c.name}
            className={`${c.color} rounded-2xl p-4 flex flex-col items-start gap-2`}
          >
            <span className="text-2xl">{c.emoji}</span>
            <div>
              <p className="text-foreground text-sm font-semibold">{c.name}</p>
              <p className="text-muted-foreground text-[10px]">
                {c.count} spots
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───────── Screen data ───────── */
const SCREENS = [
  {
    id: "hero",
    tagline: "find your quiet spot",
    subtitle: "work your way, anywhere.",
    Component: HeroScreen,
  },
  {
    id: "map",
    tagline: "100+ quiet spots across London",
    subtitle: "discover spots near you on the map.",
    Component: MapScreen,
  },
  {
    id: "detail",
    tagline: "every detail, before you go",
    subtitle: "noise, wifi, power & more at a glance.",
    Component: SpotDetailScreen,
  },
  {
    id: "search",
    tagline: "search by area or name",
    subtitle: "kings cross, shoreditch, islington…",
    Component: SearchScreen,
  },
  {
    id: "categories",
    tagline: "cafés, hotels, libraries & more",
    subtitle: "browse 6 categories of work spots.",
    Component: CategoriesScreen,
  },
];

/* ───────── Main Page ───────── */
const Carousel = () => {
  const screenRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [current, setCurrent] = useState(0);

  const downloadScreen = useCallback(async (index: number) => {
    const node = screenRefs.current[index];
    if (!node) return;
    try {
      const dataUrl = await toPng(node, {
        width: 1290,
        height: 2796,
        style: {
          transform: "scale(3)",
          transformOrigin: "top left",
          width: "430px",
          height: "932px",
        },
        pixelRatio: 1,
      });
      const link = document.createElement("a");
      link.download = `qwip-appstore-${SCREENS[index].id}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Export failed:", err);
    }
  }, []);

  const downloadAll = useCallback(async () => {
    setDownloading(true);
    for (let i = 0; i < SCREENS.length; i++) {
      await downloadScreen(i);
      await new Promise((r) => setTimeout(r, 500));
    }
    setDownloading(false);
  }, [downloadScreen]);

  const goTo = (dir: number) => {
    setCurrent((c) => Math.max(0, Math.min(SCREENS.length - 1, c + dir)));
  };

  return (
    <div className="min-h-screen bg-foreground">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-foreground/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="funnel-display text-background text-xl font-bold">
            qwip — app store screens
          </h1>
          <p className="text-background/50 text-xs mt-0.5">
            {current + 1} / {SCREENS.length} · click arrows or use keyboard
          </p>
        </div>
        <button
          onClick={downloadAll}
          disabled={downloading}
          className="flex items-center gap-2 bg-background text-foreground text-sm font-medium px-4 py-2 rounded-full hover:bg-background/90 transition disabled:opacity-50"
        >
          <Download size={14} />
          {downloading ? "Exporting…" : "Download All PNGs"}
        </button>
      </div>

      {/* Carousel view */}
      <div className="flex items-center justify-center min-h-[calc(100vh-72px)] relative">
        {/* Nav arrows */}
        <button
          onClick={() => goTo(-1)}
          disabled={current === 0}
          className="absolute left-4 lg:left-12 z-20 text-background/40 hover:text-background transition disabled:opacity-20"
        >
          <ChevronLeft size={40} />
        </button>
        <button
          onClick={() => goTo(1)}
          disabled={current === SCREENS.length - 1}
          className="absolute right-4 lg:right-12 z-20 text-background/40 hover:text-background transition disabled:opacity-20"
        >
          <ChevronRight size={40} />
        </button>

        {/* Screen */}
        {SCREENS.map((screen, i) => {
          const { Component } = screen;
          return (
            <div
              key={screen.id}
              className={`flex flex-col items-center gap-8 transition-all duration-500 ${
                i === current
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-95 absolute pointer-events-none"
              }`}
            >
              {/* Exportable container */}
              <div
                ref={(el) => {
                  screenRefs.current[i] = el;
                }}
                className="flex flex-col items-center justify-center gap-8 px-16 py-12"
                style={{
                  width: 430,
                  height: 932,
                  background: "#1f3d32",
                }}
              >
                <IPhoneFrame>
                  <Component />
                </IPhoneFrame>
                {/* Tagline below phone */}
                <div className="text-center">
                  <p className="funnel-display text-background text-2xl font-bold leading-tight">
                    {screen.tagline}
                  </p>
                  <p className="font-cormorant italic text-background/60 text-base mt-1">
                    {screen.subtitle}
                  </p>
                </div>
              </div>
              {/* Download single */}
              <button
                onClick={() => downloadScreen(i)}
                className="text-background/40 hover:text-background text-xs flex items-center gap-1 transition"
              >
                <Download size={12} />
                Download this screen
              </button>
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {SCREENS.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-all ${
              i === current
                ? "bg-background w-6"
                : "bg-background/30 hover:bg-background/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
